<script setup lang="ts">
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex,
	BreadcrumbProps
} from '.';

import {
	BreadcrumbItemWrapper,
	BreadcrumbList,
	BreadcrumbWrapper,
	isFirstOrLastItem
} from '.';
import Skeleton from '../skeleton/Skeleton.vue';

const props = withDefaults(defineProps<BreadcrumbProps>(), {
	isLoading: false
});

const emit = defineEmits<{
	(e: 'breadcrumb-clicked', payload: BreadcrumbItemWithIndex): void;
}>();

const mobileBreadcrumbItems = computed<BreadcrumbItem[]>(() => {
	return props.items.filter(
		(_, index) => !isFirstOrLastItem(index, props.items)
	);
});

function getItemKey(label: BreadcrumbItem['label']) {
	return `breadcrumb-item-${label}`;
}

function emitEvent(item: BreadcrumbItemWithIndex) {
	if (item.index === props.items.length - 1) {
		return;
	}
	emit('breadcrumb-clicked', item);
}
</script>

<template>
	<Skeleton :is-active="isLoading" class="min-h-4 w-full" variant="white">
		<BreadcrumbWrapper v-if="!isLoading" class="hidden sm:block">
			<BreadcrumbList class="text-base">
				<BreadcrumbItemWrapper
					v-for="(item, index) in items"
					:key="getItemKey(item.label)"
					:is-mobile="false"
					:items="items"
					:mobile-breadcrumb-items="mobileBreadcrumbItems"
					:item="item"
					:index="index"
					@breadcrumb-clicked="emitEvent({ ...item, index })"
				/>
			</BreadcrumbList>
		</BreadcrumbWrapper>
		<BreadcrumbWrapper v-if="!isLoading" class="block sm:hidden">
			<BreadcrumbList class="text-base">
				<ClientOnly>
					<BreadcrumbItemWrapper
						v-for="(item, index) in items"
						:key="getItemKey(item.label)"
						:is-mobile="true"
						:items="items"
						:mobile-breadcrumb-items="mobileBreadcrumbItems"
						:item="item"
						:index="index"
						@breadcrumb-clicked="emitEvent({ ...item, index })"
					/>
					<template #fallback>
						<div
							v-for="item in mobileBreadcrumbItems"
							:key="getItemKey(item.label)"
							class="group contents"
						>
							<Skeleton class="h-6 w-16" />
							<BreadcrumbSeparator
								class="cursor-default text-primary-light-1 group-last:hidden"
							/>
						</div>
					</template>
				</ClientOnly>
			</BreadcrumbList>
		</BreadcrumbWrapper>
	</Skeleton>
</template>
